import UserConfigService from '@/core/services/UserConfigService'

const Z_PASSIVE = 100000
const Z_ACTIVE = 100100
const Z_PASSIVE_AOT = 100200
const Z_ACTIVE_AOT = 100300

const Z_FLOAT_PASSIVE = 99400
const Z_FLOAT_ACTIVE = 99500
const Z_FLOAT_PASSIVE_AOT = 99600
const Z_FLOAT_ACTIVE_AOT = 99700

const panels = {}
const mountedPanels = {}
const shortcuts = {}
let _shortcutListenerActive = false
let activePanelId = null
let _migrated = false
let _shakeHiddenPanelIds = []
let _shakeHiddenActive = false
const _listeners = []

function _notify(event, panelId) {
    _listeners.forEach(fn => fn(event, panelId))
}

export function onPanelChange(fn) {
    _listeners.push(fn)
    return () => { _listeners.splice(_listeners.indexOf(fn), 1) }
}

export function isPanelOpen(panelId) {
    return !!panels[panelId]
}

export function isPanelVisible(panelId) {
    return !!mountedPanels[panelId]?.visible
}

export function getPanelInstance(panelId) {
    return mountedPanels[panelId] || null
}

export function getMountedPanelIds() {
    return Object.keys(mountedPanels)
}

function nsKey(panelId) {
    return 'panel:' + panelId
}

function migrateOnce() {
    if (_migrated) return
    _migrated = true

    try {
        const raw = localStorage.getItem('panelManagerState')
        if (raw) {
            const old = JSON.parse(raw)
            for (const panelId in old) {
                const data = old[panelId]
                if (data.alwaysOnTop !== undefined) {
                    UserConfigService.setKey(nsKey(panelId), 'alwaysOnTop', !!data.alwaysOnTop)
                }
                if (data.openOnLoad !== undefined) {
                    UserConfigService.setKey(nsKey(panelId), 'openOnLoad', !!data.openOnLoad)
                }
            }
            localStorage.removeItem('panelManagerState')
        }
    } catch { /* ignore */ }

    try {
        const raw = localStorage.getItem('noteApp')
        if (raw) {
            const old = JSON.parse(raw)
            UserConfigService.set(nsKey('noteApp'), {
                floatingMode: old.floatingMode || false,
                x: old.x ?? null,
                y: old.y ?? null,
                w: old.width || null,
                h: old.height || null,
                sidebarWidth: old.sidebarWidth || null,
                listWidth: old.listWidth || null,
                includeShared: old.includeShared || false,
            })
            localStorage.removeItem('noteApp')
        }
    } catch { /* ignore */ }

    try {
        const raw = localStorage.getItem('agendaApp')
        if (raw) {
            const old = JSON.parse(raw)
            UserConfigService.set(nsKey('agendaPanel'), {
                floatingMode: old.floatingMode || false,
                x: old.x ?? null,
                y: old.y ?? null,
                w: old.width || null,
                h: old.height || null,
                sidebarWidth: old.sidebarWidth || null,
                sidebarGrouped: old.sidebarGrouped !== undefined ? old.sidebarGrouped : true,
                showShared: old.showShared !== undefined ? old.showShared : true,
            })
            localStorage.removeItem('agendaApp')
        }
    } catch { /* ignore */ }
}

function recalculate() {
    for (const id in panels) {
        const panel = panels[id]
        const isActive = id === activePanelId
        const aot = panel.alwaysOnTop
        const floating = !!mountedPanels[id]?.floatingMode
        let zBase
        if (floating) {
            if (isActive && aot) zBase = Z_FLOAT_ACTIVE_AOT
            else if (!isActive && aot) zBase = Z_FLOAT_PASSIVE_AOT
            else if (isActive && !aot) zBase = Z_FLOAT_ACTIVE
            else zBase = Z_FLOAT_PASSIVE
        } else {
            if (isActive && aot) zBase = Z_ACTIVE_AOT
            else if (!isActive && aot) zBase = Z_PASSIVE_AOT
            else if (isActive && !aot) zBase = Z_ACTIVE
            else zBase = Z_PASSIVE
        }
        panel.callback(zBase)
    }
}

export function register(panelId, callback) {
    migrateOnce()
    const alwaysOnTop = !!UserConfigService.getKey(nsKey(panelId), 'alwaysOnTop', false)
    panels[panelId] = { callback, alwaysOnTop }
    activePanelId = panelId
    recalculate()
    _notify('open', panelId)
    return alwaysOnTop
}

export function unregister(panelId) {
    if (_shakeHiddenActive && !_shakeHiddenPanelIds.includes(panelId)) {
        restoreShakeHidden()
    }
    if (_shakeHiddenActive) {
        _shakeHiddenPanelIds = _shakeHiddenPanelIds.filter(id => id !== panelId)
        if (!_shakeHiddenPanelIds.length) _shakeHiddenActive = false
    }
    delete panels[panelId]
    if (activePanelId === panelId) {
        const ids = Object.keys(panels)
        activePanelId = ids.length ? ids[ids.length - 1] : null
    }
    recalculate()
    _notify('close', panelId)
}

export function bringToFront(panelId) {
    if (!panels[panelId]) return
    if (activePanelId === panelId) return
    activePanelId = panelId
    recalculate()
}

export function notifyFloatingChanged() {
    recalculate()
}

export function setAlwaysOnTop(panelId, value) {
    if (!panels[panelId]) return
    panels[panelId].alwaysOnTop = value
    UserConfigService.setKey(nsKey(panelId), 'alwaysOnTop', value)
    recalculate()
}

export function setOpenOnLoad(panelId, value) {
    UserConfigService.setKey(nsKey(panelId), 'openOnLoad', value)
}

export function getOpenOnLoad(panelId) {
    migrateOnce()
    return !!UserConfigService.getKey(nsKey(panelId), 'openOnLoad', false)
}

export function saveLayout(panelId, data) {
    UserConfigService.set(nsKey(panelId), data)
}

export function loadLayout(panelId) {
    migrateOnce()
    return UserConfigService.get(nsKey(panelId))
}

export function mountPanel(panelId, instance) {
    mountedPanels[panelId] = instance
}

export function unmountPanel(panelId) {
    delete mountedPanels[panelId]
    unregisterShortcut(panelId)
}

function onShortcutKeydown(e) {
    if (!e.altKey) return
    const entry = shortcuts[e.code]
    if (!entry) return
    e.preventDefault()
    togglePanel(entry.panelId)
}

export function registerShortcut(panelId, shortcut) {
    if (!shortcut || !shortcut.code) return
    shortcuts[shortcut.code] = { panelId }
    if (!_shortcutListenerActive) {
        document.addEventListener('keydown', onShortcutKeydown)
        _shortcutListenerActive = true
    }
}

function unregisterShortcut(panelId) {
    for (const code in shortcuts) {
        if (shortcuts[code].panelId === panelId) delete shortcuts[code]
    }
}

export function openAutoLoadPanels() {
    migrateOnce()
    for (const panelId in mountedPanels) {
        if (getOpenOnLoad(panelId)) {
            const instance = mountedPanels[panelId]
            if (!instance || typeof instance.open !== 'function') continue
            if (!instance.canAutoOpen) continue
            instance.open()
        }
    }
}

export function openPanel(panelId, context) {
    const instance = mountedPanels[panelId]
    if (!instance || typeof instance.open !== 'function') return
    instance.open()
    if (context && typeof instance.onContext === 'function') {
        instance.onContext(context)
    }
}

export function togglePanel(panelId) {
    const instance = mountedPanels[panelId]
    if (!instance || typeof instance.toggle !== 'function') return
    instance.toggle()
}

function restoreShakeHidden() {
    for (const id of _shakeHiddenPanelIds) {
        const inst = mountedPanels[id]
        if (inst && typeof inst.setShakeHidden === 'function') inst.setShakeHidden(false)
    }
    _shakeHiddenPanelIds = []
    _shakeHiddenActive = false
}

export function shakeToggleOtherPanels(shakerPanelId) {
    if (!_shakeHiddenActive) {
        const toHide = []
        for (const id in mountedPanels) {
            if (id === shakerPanelId) continue
            const inst = mountedPanels[id]
            if (inst && inst.floatingMode && inst.visible && !inst.shakeHidden) {
                inst.setShakeHidden(true)
                toHide.push(id)
            }
        }
        if (toHide.length) {
            _shakeHiddenPanelIds = toHide
            _shakeHiddenActive = true
        }
    } else {
        restoreShakeHidden()
    }
}

export function clearShakeHidden(panelId) {
    _shakeHiddenPanelIds = _shakeHiddenPanelIds.filter(id => id !== panelId)
    if (!_shakeHiddenPanelIds.length) _shakeHiddenActive = false
}

export default {
    register,
    unregister,
    bringToFront,
    notifyFloatingChanged,
    setAlwaysOnTop,
    setOpenOnLoad,
    getOpenOnLoad,
    saveLayout,
    loadLayout,
    mountPanel,
    unmountPanel,
    registerShortcut,
    openAutoLoadPanels,
    openPanel,
    togglePanel,
    shakeToggleOtherPanels,
    clearShakeHidden,
    onPanelChange,
    isPanelOpen,
    isPanelVisible,
    getPanelInstance,
    getMountedPanelIds,
}
